import isEmail from 'validator/lib/isEmail';
import isURL from 'validator/lib/isURL';

import { isMisspelledDomain } from 'bundles/authentication-common/utils/emailVerificationUtils';
import {
  EMAIL_MAX_LENGTH,
  USER_EMAIL_REGEX_PATTERN,
  USER_NAME_MAX_LENGTH,
  USER_NAME_MIN_LENGTH,
  USER_PASSWORD_MAX_LENGTH,
  USER_PASSWORD_MIN_LENGTH,
} from 'bundles/user-account/common/constants';

import _t from 'i18n!nls/authentication';

export const getEmailErrors = () => ({
  invalidEmail: _t('Invalid email. Please enter email as name@email.com'),
  blankEmail: _t('Please provide an email in the input below.'),
  misspelledEmailDomain: _t('Looks like you misspelled the domain of your email address. Please try again.'),
});

const EMAIL_CONSTRAINT = (email: string) => {
  const regex = new RegExp(USER_EMAIL_REGEX_PATTERN);
  const EMAIL_ERRORS = getEmailErrors();

  if (!email) {
    return EMAIL_ERRORS.blankEmail;
  }

  if (email.length > EMAIL_MAX_LENGTH) {
    return _t('Email must be shorter than #{maxLength} characters.', {
      maxLength: EMAIL_MAX_LENGTH,
    });
  }

  if (!regex.test(email) || !isEmail(email)) {
    return EMAIL_ERRORS.invalidEmail;
  }

  if (email && email.includes('@')) {
    const emailSplit = email.split('@');
    const emailProvider = emailSplit[emailSplit.length - 1];

    // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    return isMisspelledDomain(emailProvider) && EMAIL_ERRORS.misspelledEmailDomain;
  }

  return false;
};

const validateName = (name: string, fieldName: string) => {
  if (name.length < USER_NAME_MIN_LENGTH) {
    return _t(`#{fieldName} is required.`, { fieldName });
  }

  if (name.length > USER_NAME_MAX_LENGTH) {
    return _t(`#{fieldName} must contain between #{minLength} and #{maxLength} characters.`, {
      fieldName,
      minLength: USER_NAME_MIN_LENGTH,
      maxLength: USER_NAME_MAX_LENGTH,
    });
  }

  if (isEmail(name) || isURL(name)) {
    return _t(`#{fieldName} should not be an email address or URL.`, {
      fieldName,
    });
  }

  return false;
};

const FIRSTNAME_CONSTRAINT = (name: string) => validateName(name, 'First name');
const LASTNAME_CONSTRAINT = (name: string) => validateName(name, 'Last name');
const FULLNAME_CONSTRAINT = (name: string) => validateName(name, 'Full name');

const PASSWORD_CONSTRAINT = (password: string) => {
  if (password.length > USER_PASSWORD_MAX_LENGTH || password.length < USER_PASSWORD_MIN_LENGTH) {
    return _t('Password must contain between 8 and 72 characters.');
  }

  return false;
};

const TOU_CHECKBOX_CONSTRAINT = (checked: string) => {
  if (checked !== 'true') {
    return _t('Must accept Terms of Use and Privacy Notice.');
  }

  return false;
};

const NOT_EMPTY_CONSTRAINT = (value: string) => {
  if (!value) {
    return _t('This field is required.');
  }

  return false;
};

export {
  EMAIL_CONSTRAINT,
  FIRSTNAME_CONSTRAINT,
  LASTNAME_CONSTRAINT,
  FULLNAME_CONSTRAINT,
  PASSWORD_CONSTRAINT,
  TOU_CHECKBOX_CONSTRAINT,
  NOT_EMPTY_CONSTRAINT,
};
